// Territory Aliases — super-admin utility. Lists territories currently
// on DIDs that don't map to a partner and lets one click-link them to
// an existing org (adds to that org's `aliases[]` and re-links matching
// DIDs immediately). No code deploy needed to fix future imports.
//
// Iter 88o: Alias Preview — an "Eye" button now dry-runs the alias
// against `POST /organizations/<org>/aliases/preview` and shows the
// exact DID rows that would be re-linked. Confirming inside the
// preview dialog performs the actual link.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, Loader2, Search, CheckCircle2, ArrowRight, Eye, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function TerritoryAliases() {
  const { user } = useAuth();
  const [rows, setRows]           = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [picks, setPicks]         = useState({});     // territory → org_id
  const [busyKeys, setBusyKeys]   = useState(new Set());
  const [preview, setPreview]     = useState(null);   // {territory, org, data} | null
  const [previewLoading, setPreviewLoading] = useState(false);

  const isSuper = user?.role === "super_admin";

  const load = async () => {
    try {
      setLoading(true);
      const [tres, ores] = await Promise.all([
        api.get("/organizations/unmatched-territories", { params: { limit: 500 } }),
        api.get("/organizations", { params: { type: "partner" } }),
      ]);
      setRows(tres.data.items || []);
      setOrgs((ores.data || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openPreview = async (territory, orgId) => {
    if (!orgId) { toast.error("Pick a partner first"); return; }
    const org = orgs.find((o) => o.org_id === orgId);
    setPreview({ territory, org, data: null });
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/aliases/preview`,
                                      { aliases: [territory] });
      setPreview({ territory, org, data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const linkOne = async (territory, orgId) => {
    if (!orgId) { toast.error("Pick a partner first"); return; }
    setBusyKeys((s) => new Set(s).add(territory));
    try {
      const { data } = await api.post(`/organizations/${orgId}/aliases`,
                                      { aliases: [territory] });
      toast.success(`Linked ${data.relinked || 0} DIDs`);
      setRows((rs) => rs.filter((r) => r.territory !== territory));
      setPicks((p) => { const n = { ...p }; delete n[territory]; return n; });
      setPreview(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed");
    } finally {
      setBusyKeys((s) => { const n = new Set(s); n.delete(territory); return n; });
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.territory || "").toLowerCase().includes(s));
  }, [rows, q]);

  if (!isSuper) {
    return (
      <div data-testid="territory-aliases-page">
        <PageHeader title="Territory aliases" subtitle="Super admin only." />
        <Card className="p-8 text-center text-sm text-slate-500">
          Ask a super admin to open this page — it maps territory strings on
          DIDs to a partner org so future imports auto-link them.
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="territory-aliases-page">
      <PageHeader
        testid="territory-aliases-header"
        title="Territory aliases"
        subtitle="Link the territory strings the last import couldn't recognise. Preview shows the exact DIDs that will be relinked before you commit."
      >
        <Button variant="outline" onClick={load} disabled={loading}
                data-testid="territory-refresh">
          <Loader2 className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </PageHeader>

      <div className="mb-3 flex items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input className="pl-8" placeholder="Filter territories…"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="territory-filter" />
        </div>
        <span className="text-xs text-slate-500" data-testid="territory-count">
          {loading ? "Loading…" : `${filtered.length} unmatched territor${filtered.length === 1 ? "y" : "ies"}`}
        </span>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/60 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Territory</th>
              <th className="px-4 py-2 text-right">DIDs</th>
              <th className="px-4 py-2">Sample</th>
              <th className="px-4 py-2 min-w-[280px]">Link to partner</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="territory-rows">
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-emerald-600"
                      data-testid="territory-none">
                <CheckCircle2 className="mx-auto h-5 w-5" />
                <div className="mt-1">Nothing left to link — every DID already has a partner.</div>
              </td></tr>
            ) : filtered.map((r) => {
              const busy = busyKeys.has(r.territory);
              const pick = picks[r.territory] || "";
              return (
                <tr key={r.territory} className="border-b border-slate-50 hover:bg-slate-50/40"
                    data-testid={`territory-row-${r.territory}`}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.territory}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.count.toLocaleString()}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">+{r.sample}</td>
                  <td className="px-4 py-2">
                    <select className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
                            value={pick}
                            onChange={(e) => setPicks((p) => ({ ...p, [r.territory]: e.target.value }))}
                            data-testid={`territory-org-picker-${r.territory}`}>
                      <option value="">— pick a partner —</option>
                      {orgs.map((o) => (
                        <option key={o.org_id} value={o.org_id}>{o.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline"
                              onClick={() => openPreview(r.territory, pick)}
                              disabled={!pick || busy}
                              data-testid={`territory-preview-${r.territory}`}
                              title="Preview DIDs that will be linked">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" onClick={() => linkOne(r.territory, pick)}
                              disabled={!pick || busy}
                              data-testid={`territory-link-${r.territory}`}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <><LinkIcon className="h-3.5 w-3.5" /> Link <ArrowRight className="h-3.5 w-3.5" /></>}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Iter 88o: Alias Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="alias-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-emerald-600" /> Preview alias link
            </DialogTitle>
            <DialogDescription>
              Adding <b>&ldquo;{preview?.territory}&rdquo;</b> as an alias on{" "}
              <b>{preview?.org?.name}</b> will re-link the DIDs below.
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !preview?.data ? (
            <div className="py-8 text-center text-slate-400">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-700 flex items-center gap-4">
                <div>
                  <div className="font-semibold text-slate-900" data-testid="alias-preview-count">
                    {preview.data.pending_count}
                  </div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                    DIDs matched
                  </div>
                </div>
                <div className="border-l border-slate-200 pl-4">
                  <div className="font-semibold text-slate-900">
                    {preview.data.new_aliases?.length || 0}
                  </div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                    New alias{(preview.data.new_aliases?.length || 0) === 1 ? "" : "es"}
                  </div>
                </div>
                {(preview.data.already_present?.length || 0) > 0 && (
                  <div className="border-l border-slate-200 pl-4 text-amber-700">
                    <div className="font-semibold">{preview.data.already_present.length}</div>
                    <div className="text-[11px] uppercase tracking-wider">already on org</div>
                  </div>
                )}
              </div>
              {preview.data.pending_count === 0 ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 space-y-1"
                     data-testid="alias-preview-empty">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="h-4 w-4" />
                    Nothing matches right now &mdash; but this alias still earns its keep.
                  </div>
                  <div className="text-[12px] leading-relaxed text-emerald-900/80 pl-6">
                    Every future CSV import, DID feed sync, or manual add whose
                    <code className="mx-1 rounded bg-emerald-100 px-1">territory</code>
                    field equals <b>&ldquo;{preview.territory}&rdquo;</b> will
                    auto-link to <b>{preview.org?.name}</b> from now on &mdash;
                    no more staff clean-up trips through this page for the
                    same string.
                  </div>
                </div>
              ) : (
                <div className="max-h-80 overflow-auto rounded-md border border-slate-100"
                     data-testid="alias-preview-list">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">DID</th>
                        <th className="px-3 py-2 text-left">Carrier</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.data.pending_dids.map((d) => (
                        <tr key={d.did_number} className="border-b border-slate-50"
                            data-testid={`alias-preview-did-${d.did_number}`}>
                          <td className="px-3 py-1.5 font-mono">+{d.did_number}</td>
                          <td className="px-3 py-1.5 text-slate-600">{d.carrier || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-600">{d.did_type || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-600">{d.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}
                    data-testid="alias-preview-cancel">
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
            <Button onClick={() => linkOne(preview?.territory, preview?.org?.org_id)}
                    disabled={!preview?.data || previewLoading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="alias-preview-confirm">
              <LinkIcon className="h-3.5 w-3.5" />
              {(preview?.data?.pending_count || 0) === 0
                ? "Save alias for future imports"
                : `Confirm & link ${preview?.data?.pending_count} DID${preview?.data?.pending_count === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
